import { action, computed } from 'mobx'
import { orderBy } from 'lodash-es'
import { type Job } from '@/features/jobs/Job'
import { type JobScopeChange } from '@/features/jobs/job-scope-changes/JobScopeChange'

/**
 * View model for the banner announcing the job's pending scope change.
 */
export class ScopeChangeBannerViewModel {
  constructor(
    private readonly _job: Job,
    private readonly _getRequests: () => JobScopeChange[],
    private readonly _viewRequest: (request: JobScopeChange) => void,
  ) {}

  /**
   * The latest request still waiting on someone.
   */
  @computed
  get request(): JobScopeChange | undefined {
    return orderBy(
      this._getRequests().filter((r) => r.isPending),
      'createTime',
      'desc',
    )[0]
  }

  /**
   * Who the pending request is waiting on.
   */
  @computed
  get title() {
    switch (this.request?.status) {
      case 'PendingClientApproval':
        return 'Scope change waiting on the client'
      case 'PendingAdminApproval':
        return 'Scope change waiting on an admin'
      default:
        return 'Scope change pending'
    }
  }

  /**
   * Who asked for which items.
   */
  @computed
  get summary() {
    const request = this.request
    if (!request) {
      return ''
    }
    const requester = request.isCreatedByAdmin
      ? 'An admin'
      : request.createdByUserPublicId ===
          this._job.currentUserCollaborator?.member.user.publicId
        ? 'You'
        : (this._job.collaborators.find(
            (c) => c.member.user.publicId === request.createdByUserPublicId,
          )?.member.displayName ?? 'Someone on the job')
    const items = request.items.map((item) =>
      item.quantity > 1 ? `${item.name} ×${item.quantity}` : item.name,
    )
    const what =
      items.length === 0
        ? 'a change to the job scope'
        : items.length <= 2
          ? items.join(' and ')
          : `${items.slice(0, 2).join(', ')} and ${items.length - 2} more`
    return `${requester} asked for ${what}.`
  }

  /**
   * Opens the pending request.
   */
  @action.bound
  viewRequest() {
    if (this.request) {
      this._viewRequest(this.request)
    }
  }
}
